"use client"

import React, { Fragment, useEffect, useRef } from "react"
import NextLink, { type LinkProps as NextLinkProps } from "next/link"
import TC_Wrapper from "react-tag-commander"

import { buildAnalyticsLinks, cn } from "@/lib/utils"
import {
  baseButtonVariants,
  IBaseButtonVariantProps,
} from "@/components/ui/atoms/baseButton/BaseButton"
import { RemixIcon, TIconSize } from "@/components/ui/atoms"

type TLinkProps = NextLinkProps &
  IBaseButtonVariantProps & {
    asChild?: boolean
    id?: string
    className?: string
    iconSize?: TIconSize
    title?: string
    chapters: (string | undefined)[]
    analyticsTag?: string
    analyticsType?: string
    navigationType?: string
    target?: string
    rel?: string
  }

type BuiltChapters = {
  chapters: {
    click: string
    click_chapter1: string
    click_chapter2: string
    click_chapter3: string
    click_type?: string
    click_tag?: string
  }
  type: string
}

const Link = ({
  variant,
  iconLeft,
  iconRight,
  iconSize,
  children,
  href,
  className,
  title,
  analyticsTag,
  analyticsType,
  chapters,
  navigationType,
  target,
  rel,
  ...props
}: TLinkProps) => {
  // Modern browsers imply noopener/noreferrer for target="_blank", but
  // older ones do not, so make it explicit unless the caller overrides it.
  const safeRel = target === "_blank" ? (rel ?? "noopener noreferrer") : rel

  const linkRef = useRef<HTMLAnchorElement>(null)

  useEffect(() => {
    // Get origin url from browser
    const wrapper = TC_Wrapper.getInstance()

    const generateNavigationEvent = (event: globalThis.MouseEvent) => {
      // Ensure that event.currentTarget is an instance of HTMLAnchorElement
      if (!(event.currentTarget instanceof HTMLAnchorElement)) return

      // Convert href to a URL string, handling both string and UrlObject cases.
      // Note:
      // This does not handle query parameters in the UrlObject case. Need to update the logic if query parameters are needed.
      const urlString =
        typeof href === "string"
          ? new URL(href, window.location.origin).toString()
          : `${window.location.origin}${href.pathname}`

      const analytics = buildAnalyticsLinks(
        chapters,
        new URL(urlString),
        navigationType,
        analyticsType,
        analyticsTag,
        window.location.host
      )

      wrapper.triggerEvent(
        analytics.type,
        event.currentTarget,
        analytics.chapters
      )
    }

    // Attach the event listener
    const currentLink = linkRef.current
    if (currentLink) {
      currentLink.addEventListener("click", generateNavigationEvent)
    }

    // Cleanup function to remove the event listener
    return () => {
      if (currentLink) {
        currentLink.removeEventListener("click", generateNavigationEvent)
      }
    }
  }, [chapters, analyticsTag, analyticsType, href, navigationType])

  const linkContent = (
    <Fragment>
      {/* click:{analytics?.click ? analytics?.click : children}/chapter1:{analytics?.click_chapter1 ? analytics?.click_chapter1 : title}/ */}
      {iconLeft ? (
        <RemixIcon
          name={iconLeft}
          size="md"
          className="shrink-0 fill-current"
        />
      ) : null}
      {children}
      {iconRight ? (
        <RemixIcon
          name={iconRight}
          size="md"
          className="shrink-0 fill-current"
        />
      ) : null}
    </Fragment>
  )

  return (
    <NextLink
      ref={linkRef}
      className={cn(baseButtonVariants({ variant, className }), {
        "gap-3": (iconLeft || iconRight) && children,
        "baseButton--icon": (iconLeft || iconRight) && !children,
      })}
      aria-label={title}
      href={href}
      target={target}
      rel={safeRel}
      {...props}
    >
      {linkContent}
    </NextLink>
  )
}

export { Link, type TLinkProps, type BuiltChapters }
